import { Button } from "react-bootstrap";
import { useIntl } from "react-intl";
import { CreateOrdersForm, SimpleModal } from "../../components";
import { useState } from "react";
import { Form, Formik } from "formik";
import {
    useFrequencyOptions,
    useSiteByManagerOptions,
    useThematicsOptions,
    useWordCountOptions,
    useWritingTypeOptions,
} from "src/app/hooks";
import {
    CreateOrders,
    CreateOrdersBoostSchema,
    CreateOrdersSchema,
} from "src/app/types/CreateOrders";
import { toast } from "react-toastify";
import { OrderType, TOrderType, WritingType } from "src/app/types/orders/order";
import { OrderTransport } from "src/app/transports/order";
import { KeywordsTransport } from "src/app/transports/keywords";
import { getKeywordFromString } from "src/app/utils/keywordsHelper";
import { find } from "lodash";
export interface ICreateOrdersModalContainer {
    hasMoneySiteId?: boolean;
    orderType: TOrderType;
}

const CreateOrdersModalContainer = (props: ICreateOrdersModalContainer) => {
    const { hasMoneySiteId = true, orderType } = props;

    const { formatMessage } = useIntl();

    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [badKeywords, setBadKeywords] = useState<Array<string>>([]);

    const { siteByManagerOptions, loading: siteByManagerLoading } =
        useSiteByManagerOptions(hasMoneySiteId);

    const { thematicsOptions, loading: thematicsOptionsLoading } = useThematicsOptions(
        !hasMoneySiteId
    );
    const { frequencyOptions } = useFrequencyOptions();
    const { wordCountOptions } = useWordCountOptions();
    const { writingTypeOptions } = useWritingTypeOptions([
        WritingType.AI_AUTO,
        WritingType.AI_MANUAL,
    ]);

    const isRankingOrder = OrderType.RANK === orderType;
    const schema = orderType === OrderType.BOOST ? CreateOrdersBoostSchema : CreateOrdersSchema;

    async function checkKeywordVolume(keywordsString: string): Promise<boolean> {
        const keywords = getKeywordFromString(keywordsString);

        const response = await KeywordsTransport.checkVolumeInSemrush(keywords);

        const currentBadKeywords: Array<string> = [];

        for (const key in response) {
            if (response[key] < 4000) {
                currentBadKeywords.push(key);
            }
        }
        if (currentBadKeywords.length === 0) return true;

        setBadKeywords((oldArray) => [...oldArray, ...currentBadKeywords]);
        toast.error(
            formatMessage(
                { id: "common.notification.error.keywordWithVolumeUnder4000" },
                { keyword: currentBadKeywords.join(", ") }
            )
        );

        return false;
    }

    async function handleSubmit(_values: CreateOrders) {
        setLoading(true);

        try {
            if (isRankingOrder) {
                const validKeywordVolume = await checkKeywordVolume(_values.keywords);

                if (!validKeywordVolume) {
                    return;
                }
            }

            await OrderTransport.createOrders(_values, orderType);
            toast.success(formatMessage({ id: "common.notification.success" }));
            setShowModal(false);
        } catch (err: any) {
            toast.error(formatMessage({ id: "common.notification.error" }));
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <Button onClick={() => setShowModal(true)}>
                {formatMessage({
                    id: `dashboard.button.${orderType}`,
                })}
            </Button>
            {showModal && (
                <Formik
                    initialValues={schema.getDefault()}
                    validationSchema={schema}
                    onSubmit={(values: object) => handleSubmit(values as CreateOrders)}
                >
                    {(formikProps) => {
                        let hasBadKeywords = null;
                        if (isRankingOrder) {
                            const values = formikProps.values as { keywords?: string };
                            hasBadKeywords = find(
                                badKeywords,
                                (keyword) =>
                                    values.keywords && values.keywords.indexOf(keyword) > -1
                            );
                        }

                        return (
                            <SimpleModal
                                showModal={showModal}
                                handleClose={() => setShowModal(false)}
                                entityName={formatMessage({ id: `dashboard.button.${orderType}` })}
                                formId="createOrdersModal"
                                disableConfirmation={!!hasBadKeywords}
                                loading={loading}
                            >
                                <Form id="createOrdersModal">
                                    <CreateOrdersForm
                                        moneySiteId={{
                                            options: siteByManagerOptions,
                                            loading: siteByManagerLoading,
                                        }}
                                        thematicId={{
                                            options: thematicsOptions,
                                            loading: thematicsOptionsLoading,
                                        }}
                                        isForRankOrders={isRankingOrder}
                                        hasMoneySiteId={hasMoneySiteId}
                                        frequency={{ options: frequencyOptions }}
                                        writingType={{ options: writingTypeOptions }}
                                        wordCount={{ options: wordCountOptions }}
                                    />
                                </Form>
                            </SimpleModal>
                        );
                    }}
                </Formik>
            )}
        </>
    );
};

export default CreateOrdersModalContainer;
